import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@cakrawalajakarta/shared-components";
import { db } from "@lib/firebase";
import {
  collection,
  query,
  where,
  getDoc,
  doc,
  getDocs,
  setDoc,
  deleteDoc,
  serverTimestamp,
} from "firebase/firestore";
import { useAuth } from "@context/AuthContext";
import { useNotification } from "../common/NotificationContext";
import axios from "axios";
import { InlineRegistrationForm } from "../components/InlineRegistrationForm";
import { PayPalButton } from "../common/PayPalButton";
import { VoucherInput } from "../common/VoucherInput";

// ─── Qualification Banner ─────────────────────────────────────────────────────
const getQualificationInfo = (categoryName = "") => {
  const upper = categoryName.toUpperCase();
  if (upper.includes("FUN RUN")) return null;
  if (upper.includes("RELAY")) return {
    type: "relay",
    title: "Informasi Kategori Relay",
    items: [
      "Tim terdiri dari 5 (lima) peserta.",
      "Peserta pertama yang mendaftar ditetapkan sebagai Peserta 1.",
      "Data lengkap seluruh anggota tim wajib diisi saat pendaftaran.",
      "Setiap anggota tim wajib hadir pada hari pelaksanaan.",
    ],
  };
  if (
    upper.includes("24H") ||
    upper.includes("12H") ||
    upper.includes("100KM") ||
    upper.includes("100K")
  ) {
    return {
      type: "ultra",
      title: "Syarat Kualifikasi Kategori",
      items: [
        "Peserta wajib memiliki catatan hasil lomba Full Marathon (FM) atau setara.",
        "Bukti kualifikasi berupa tautan Strava atau sertifikat finisher wajib dilampirkan.",
        "Pendaftaran akan diverifikasi oleh panitia sebelum pembayaran dibuka.",
      ],
    };
  }
  return {
    type: "medium",
    title: "Syarat Kualifikasi Kategori",
    items: [
      "Peserta wajib memiliki catatan hasil lomba Half Marathon (HM) atau setara.",
      "Bukti kualifikasi berupa tautan Strava atau sertifikat finisher wajib dilampirkan.",
      "Pendaftaran akan diverifikasi oleh panitia sebelum pembayaran dibuka.",
    ],
  };
};

const QualificationBanner = ({ categoryName }) => {
  const info = getQualificationInfo(categoryName);
  if (!info) return null;
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 px-5 py-4">
      <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">
        {info.title}
      </p>
      <ul className="space-y-1.5">
        {info.items.map((item, i) => (
          <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
            <span className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0 bg-red-600" />
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
};

const SkeletonLoader = () => (
  <div className="w-full min-h-screen py-20 text-center text-brand-red flex-col justify-center relative bg-slate-100">
    <div className="relative z-0 container mx-auto px-4">
      <div className="w-full h-[60vh] bg-gray-200 animate-pulse"></div>
      <div className="p-4 space-y-4 mt-8">
        <div className="h-6 bg-gray-200 rounded animate-pulse"></div>
        <div className="h-6 bg-gray-200 rounded animate-pulse"></div>
        <div className="h-6 bg-gray-200 rounded animate-pulse"></div>
      </div>
    </div>
  </div>
);

const formatDate = (dateString) => {
  if (!dateString) return "Tanggal belum ditentukan";
  try {
    let date;
    if (
      typeof dateString === "object" &&
      "toDate" in dateString &&
      typeof dateString.toDate === "function"
    ) {
      date = dateString.toDate();
    } else if (typeof dateString === "object" && "seconds" in dateString) {
      date = new Date(dateString.seconds * 1000);
    } else {
      date = new Date(dateString);
    }
    if (!isNaN(date.getTime())) {
      return date.toLocaleDateString("id-ID", {
        day: "numeric",
        month: "long",
        year: "numeric",
      });
    }
    if (typeof dateString === "string" && dateString.length > 0) {
      return dateString;
    }
    return "Tanggal belum ditentukan";
  } catch {
    return typeof dateString === "string" ? dateString : "Tanggal belum ditentukan";
  }
};

const formatDomesticPrice = (price) => {
  return price != null && Number(price) > 0
    ? `Rp ${Number(price).toLocaleString("id-ID")}`
    : "Gratis";
};

const formatForeignerPrice = (foreignerPrice, fallbackDomesticPrice) => {
  const value =
    foreignerPrice === undefined || foreignerPrice === null
      ? fallbackDomesticPrice
      : foreignerPrice;
  return value != null && Number(value) > 0
    ? `Rp ${Number(value).toLocaleString("id-ID")}`
    : "Gratis";
};

const formatDateTime = (value) => {
  if (!value) return "-";
  try {
    let date;
    if (typeof value === "object" && value?.toDate && typeof value.toDate === "function") {
      date = value.toDate();
    } else if (typeof value === "object" && "seconds" in value) {
      date = new Date(value.seconds * 1000);
    } else {
      date = new Date(value);
    }
    if (isNaN(date.getTime())) return "-";
    return date.toLocaleString("id-ID", {
      day: "2-digit",
      month: "short",
      year: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "-";
  }
};

const getPaymentDisplayStatus = (data = {}, overrideStatus = null) => {
  const rawStatus = (overrideStatus || data.transaction_status || "").toLowerCase();
  const paymentType = (data.payment_type || "").toLowerCase();
  const amount = Number(data.gross_amount || data.harga || 0);

  if (rawStatus === "settlement" || rawStatus === "capture") return "LUNAS";
  if (rawStatus === "pending") return "PENDING";
  if (["expire", "cancel", "deny", "failure"].includes(rawStatus)) return "GAGAL";
  if (paymentType === "free" || amount === 0) return "LUNAS";
  return "BELUM_BAYAR";
};

const getFinalPrice = (displayPrice, appliedVoucher) => {
  let finalPrice = displayPrice || 0;
  if (appliedVoucher && appliedVoucher.discount > 0) {
    if (appliedVoucher.discountType === "fixed") {
      finalPrice = displayPrice - appliedVoucher.discount;
    } else {
      finalPrice = displayPrice - (displayPrice * appliedVoucher.discount) / 100;
    }
  }
  return Math.max(0, finalPrice);
};




export const KegiatanDetail = React.memo(({ forcedSlug }) => {
  const { slug, distance } = useParams();
  // Map 'ultra24' route param to 'ULTRA24' Firestore document
  const eventSlug = useMemo(() => {
    if (forcedSlug) return forcedSlug;
    if (slug?.toLowerCase() === "ultra24") return "ULTRA24";
    return slug || "JOTR2026";
  }, [slug, forcedSlug]);
  const { user, getIdToken } = useAuth();
  const { showNotification, showConfirm } = useNotification();
  const [event, setEvent] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [collectionName, setCollectionName] = useState(null);
  const [registrationStatus, setRegistrationStatus] = useState(null);
  const [transactionStatus, setTransactionStatus] = useState(null);
  const [snapToken, setSnapToken] = useState(null);
  const [registrationData, setRegistrationData] = useState(null);
  const [isCreatingTransaction, setIsCreatingTransaction] = useState(false);
  const [isSnapReady, setIsSnapReady] = useState(false);
  const [paymentBlockedReason, setPaymentBlockedReason] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("midtrans");
  const [appliedVoucher, setAppliedVoucher] = useState(null);
  const [currentVoucherInput, setCurrentVoucherInput] = useState("");
  const [isCancelling, setIsCancelling] = useState(false);
  const currentEventIdForRules = (event?.id || eventSlug || "").toUpperCase();
  const isUltraCategorySelected = selectedCategory &&
    !selectedCategory.categoryName?.toLowerCase().includes("fun run") &&
    !selectedCategory.categoryName?.toLowerCase().includes("relay");
  const requiresAdminApproval = currentEventIdForRules === "ULTRA24"
    ? isUltraCategorySelected  // ultra categories need admin approval; fun run & relay don't
    : currentEventIdForRules !== "ULTRA24"; // keep existing logic for other events
  const isRegisteredInOtherCategory = registrationData && 
    registrationData.kategori?.trim().toLowerCase() !== selectedCategory?.categoryName?.trim().toLowerCase();

  const fetchCollectionName = useCallback(async (eventId) => {
    try {
      const eventRef = doc(db, "events", eventId);
      const eventDoc = await getDoc(eventRef);
      // STRICT MAPPING: Must exist in 'events' collection. No fallbacks.
      return eventDoc.exists() ? eventDoc.data().collectionName : null;
    } catch {
      return null;
    }
  }, []);

  const fetchEventData = useCallback(async () => {
    try {
      const eventDoc = await getDoc(doc(db, "semuaKegiatan", eventSlug));
      if (!eventDoc.exists()) {
        setError("Kegiatan tidak ditemukan");
        setLoading(false);
        return null;
      }

      const eventData = { id: eventDoc.id, ...eventDoc.data() };
      setEvent(eventData);

      // Ambil status dari level event
      const eventStatus = eventData.status || "Registrasi Dibuka";
      const isEventOpen = eventStatus === "Registrasi Dibuka";

      setLoading(false);
      
      // Calculate selected category based on distance
      let matchedCategory = null;
      if (distance && eventData.categories) {
        if (eventSlug === "ULTRA24" && eventData.categories) {
          console.log("Matching category for distance:", distance, "in event:", eventData.eventName);
          matchedCategory = eventData.categories.find((c) => {
            const catSlug = (c.categoryName || c.distance || "").toString().toLowerCase().trim().replace(/ /g, '-');
            const distSlug = distance.toLowerCase().trim();
            return catSlug === distSlug || catSlug === distSlug + "-km" || catSlug + "-km" === distSlug;
          });
          
          if (!matchedCategory && eventData.categories.length > 0) {
            matchedCategory = eventData.categories[0];
          }
        } else {
          const targetSlug = distance.toLowerCase();
          matchedCategory = eventData.categories.find(
            (cat) => cat.categoryName.toLowerCase().replace(/ /g, "-") === targetSlug
          );

          if (!matchedCategory && !isNaN(parseFloat(distance))) {
            const parsedDistance = parseFloat(distance);
            matchedCategory = eventData.categories.find((cat) => cat.distance === parsedDistance);
          }
        }
      }

      if (matchedCategory) {
        const finalCategory = {
          ...matchedCategory,
          categoryName: matchedCategory.categoryName.trim(),
          isRegistrationOpen: isEventOpen && (matchedCategory.isRegistrationOpen !== false),
        };
        setSelectedCategory(finalCategory);
        return { eventData, selectedCategory: finalCategory };
      } else {
        setError(distance ? `Kategori ${distance} tidak ditemukan` : "Parameter jarak tidak valid");
        return { eventData, selectedCategory: null };
      }
    } catch (err) {
      setError(err.message);
      console.error("[ERROR] Error fetching event data:", err);
      setLoading(false);
      return null;
    }
  }, [eventSlug, distance]);

  const checkRegistration = useCallback(
    async (collectionName, eventId, categoryName) => {
      if (!user || !eventId || !collectionName)
        return { isRegistered: false, status: null, token: null };

      try {
        const q = query(
          collection(db, collectionName),
          where("uid", "==", user.uid),
          where("eventId", "==", eventId)
        );
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
          const normalize = (v = "") =>
            String(v)
              .toLowerCase()
              .trim()
              .replace(/\s+/g, " ")
              .replace(/-/g, " ");
          const targetCategory = normalize(categoryName || "");

          const docs = querySnapshot.docs.slice().sort((a, b) => {
            const aSec = a.data()?.createdAt?.seconds || 0;
            const bSec = b.data()?.createdAt?.seconds || 0;
            return bSec - aSec;
          });

          let doc =
            docs.find((d) => normalize(d.data()?.kategori) === targetCategory) ||
            docs[0];

          const data = doc.data();
          setRegistrationData({ id: doc.id, ...data });
          const localStatus = data.transaction_status || "unknown";
          const hasToken = Boolean(data.snapToken);
          const orderId = data.orderId || doc.id;

          // Never mutate payment fields from client while checking status.
          let resolvedStatus = localStatus;
          if ((localStatus === "pending" || localStatus === "unknown") && orderId) {
            try {
              const response = await axios.get(
                `${import.meta.env.VITE_API_BASE_URL}/api/payment/status/${orderId}`,
                { params: { eventId } }
              );
              if (response?.data?.transaction_status) {
                resolvedStatus = response.data.transaction_status;
              }
            } catch {
              // keep local status as fallback
            }
          }

          setRegistrationStatus("registered");
          setTransactionStatus(resolvedStatus);
          setSnapToken(hasToken ? data.snapToken : null);
          return { isRegistered: true, status: resolvedStatus, token: hasToken ? data.snapToken : null };
        } else {
          setRegistrationStatus("not_registered");
          setRegistrationData(null);
          return { isRegistered: false, status: null, token: null };
        }
      } catch (err) {
        // Don't set page-level error for registration check failures
        // User can still view the page, just registration status is unknown
        console.error("[ERROR] Failed to check registration status:", err);
        return { isRegistered: false, status: null, token: null };
      }
    },
    [user]
  );

  useEffect(() => {
    let mounted = true;

    // Fast path: start registration check immediately from first event doc read
    // Runs in parallel with full event data processing (category matching etc.)
    const startEarlyRegCheck = async () => {
      if (!user) {
        if (mounted) setRegistrationStatus("not_registered");
        return;
      }
      try {
        const snap = await getDoc(doc(db, "semuaKegiatan", eventSlug));
        if (!snap.exists() || !mounted) return;
        const cName = snap.data().collectionName;
        if (cName && mounted) {
          setCollectionName(cName);
          await checkRegistration(cName, snap.id, null);
        }
      } catch (_) {}
    };

    // Full path: process event data + category matching, then refine reg check with category
    const fetchFullEventData = async () => {
      const response = await fetchEventData();
      if (!response || !mounted) return;
      const { eventData, selectedCategory: matchedCategory } = response;
      const resolvedEventId = eventData.id;
      if (!resolvedEventId) return;
      try {
        const fetchedCollectionName =
          eventData.collectionName || (await fetchCollectionName(resolvedEventId));
        if (mounted) {
          setCollectionName(fetchedCollectionName);
          if (fetchedCollectionName && user && matchedCategory?.categoryName) {
            checkRegistration(fetchedCollectionName, resolvedEventId, matchedCategory.categoryName);
          }
        }
      } catch {
        if (mounted) setCollectionName(null);
      }
    };

    Promise.all([startEarlyRegCheck(), fetchFullEventData()]);
    return () => {
      mounted = false;
    };
  }, [fetchEventData, fetchCollectionName, checkRegistration, user, eventSlug]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const checkSnap = setInterval(() => {
      if (window.snap) {
        setIsSnapReady(true);
        clearInterval(checkSnap);
      }
    }, 100);
    return () => clearInterval(checkSnap);
  }, []);

  const handleBayar = useCallback(async (tokenOverride = null) => {
    const activeSnapToken = tokenOverride || snapToken;
    setPaymentBlockedReason("");

    if (!isSnapReady || !window.snap) {
      showNotification("error", "Midtrans belum siap. Silakan coba lagi.");
      return;
    }

    if (!activeSnapToken) {
      showNotification("error", "Token pembayaran tidak ditemukan.");
      return;
    }

    try {
      window.snap.pay(activeSnapToken, {
        onSuccess: async () => {
          showNotification("success", "Pembayaran berhasil!");
          const { status } = await checkRegistration(
            collectionName,
            event.id,
            selectedCategory?.categoryName
          );
          setTransactionStatus(status);
        },
        onPending: async () => {
          showNotification("warning", "Pembayaran sedang diproses.");
          const { status } = await checkRegistration(
            collectionName,
            event.id,
            selectedCategory?.categoryName
          );
          setTransactionStatus(status);
        },
        onError: () => {
          showNotification("error", "Pembayaran gagal. Silakan coba lagi.");
        },
        onClose: () => {},
      });
    } catch {
      showNotification("error", "Gagal memproses pembayaran.");
    }
  }, [
    isSnapReady,
    snapToken,
    collectionName,
    event,
    showNotification,
    checkRegistration,
    selectedCategory?.categoryName
  ]);

  const ensurePaymentTransaction = useCallback(async ({ silent = false, autoPay = false } = {}) => {
    if (!registrationData || !event?.id) {
      if (!silent) showNotification("error", "Data pesanan tidak ditemukan.");
      return false;
    }
    setPaymentBlockedReason("");
    if (requiresAdminApproval && (registrationData?.administration_status || "").toLowerCase() !== "qualified") {
      if (!silent) {
        showNotification("warning", "Pendaftaran Anda masih menunggu approval admin sebelum pembayaran bisa dibuka.");
      }
      return false;
    }

    try {
      setIsCreatingTransaction(true);
      const response = await axios.post(
        `${import.meta.env.VITE_API_BASE_URL}/api/create-transaction`,
        {
          orderId: registrationData.orderId || registrationData.id,
          eventId: registrationData.eventId || event.id,
          voucherCode: appliedVoucher?.code || registrationData?.voucherCode
        }
      );

      const { snapToken: newSnapToken, status } = response.data || {};

      if (newSnapToken) {
        setSnapToken(newSnapToken);
        setTransactionStatus(status || "pending");
        if (autoPay) setTimeout(() => handleBayar(newSnapToken), 200);
        return true;
      }

      if (status === "settlement") {
        if (!silent) showNotification("success", "Pembayaran sudah terkonfirmasi.");
        setTransactionStatus("settlement");
        return true;
      } else {
        if (!silent) showNotification("error", "Gagal membuat transaksi pembayaran.");
      }

      await checkRegistration(collectionName, event.id, selectedCategory?.categoryName);
      return false;
    } catch (err) {
      const apiMessage = err?.response?.data?.message || "";
      if (apiMessage.toLowerCase().includes("belum disetujui")) {
        if (requiresAdminApproval) {
          setPaymentBlockedReason("Pendaftaran Anda masih menunggu approval admin.");
          if (!silent) showNotification("warning", "Pendaftaran Anda masih menunggu approval admin.");
        }
        return false;
      }
      if (!silent) {
        showNotification(
          "error",
          apiMessage || "Gagal membuat transaksi pembayaran."
        );
      }
      return false;
    } finally {
      setIsCreatingTransaction(false);
    }
  }, [registrationData, event, showNotification, checkRegistration, collectionName, handleBayar, requiresAdminApproval, selectedCategory?.categoryName]);

  const handleCancelRegistration = async () => {
    if (!registrationData?.id || !collectionName || !user) return;

    showConfirm(
      `Batalkan pendaftaran ${registrationData.kategori}? Seluruh progres draf saat ini akan dihapus.`,
      async () => {
        setIsCancelling(true);
        try {
          const idToken = await getIdToken();
          await axios.post(
            `${import.meta.env.VITE_API_BASE_URL}/api/registration/cancel`,
            {
              orderId: registrationData.id,
              collectionName: collectionName,
            },
            {
              headers: { Authorization: `Bearer ${idToken}` },
            }
          );

          // Reset States to trigger re-fetch/re-render
          setRegistrationData(null);
          setRegistrationStatus(null);
          setTransactionStatus(null);
          setAppliedVoucher(null);

          // Scroll to top to show the new registration form
          window.scrollTo({ top: 0, behavior: "smooth" });
          showNotification("success", "Pendaftaran berhasil dibatalkan.");
        } catch (err) {
          console.error("Error cancelling registration:", err);
          showNotification(
            "error",
            err.response?.data?.message || "Gagal membatalkan pendaftaran. Silakan coba lagi nanti."
          );
        } finally {
          setIsCancelling(false);
        }
      },
      "Konfirmasi",
      "Kembali"
    );
  };

  const handleRegistrationComplete = useCallback(
    async ({ orderId, transactionStatus: statusFromPayment, snapToken: tokenFromPayment } = {}) => {
      if (!collectionName || !event?.id) return;
      const { isRegistered, status, token } = await checkRegistration(collectionName, event.id, selectedCategory?.categoryName);
      setRegistrationStatus(isRegistered ? "registered" : "not_registered");
      setTransactionStatus(statusFromPayment || status);
      setSnapToken(tokenFromPayment || token || null);
      setPaymentBlockedReason("");
    },
    [collectionName, event, checkRegistration, selectedCategory?.categoryName]
  );

  const paymentDisplayStatus = useMemo(
    () => getPaymentDisplayStatus(registrationData || {}, transactionStatus),
    [registrationData, transactionStatus]
  );

  const handlePayNow = useCallback(async () => {
    await ensurePaymentTransaction({ silent: false, autoPay: true });
  }, [ensurePaymentTransaction]);


  const isRegistrationOpen = useMemo(() => {
    const eventStatus = event?.status || "Registrasi Dibuka";
    const isEventOpen = eventStatus === "Registrasi Dibuka";
    // Pakai nilai isRegistrationOpen dari selectedCategory
    return isEventOpen && (selectedCategory?.isRegistrationOpen !== false);
  }, [event, selectedCategory]);
  const adminStatus = (registrationData?.administration_status || "").toLowerCase();
  const isAdminApproved = adminStatus === "qualified";
  const isAdminRejected = adminStatus === "rejected";
  const isPaymentLockedByApproval =
    requiresAdminApproval && (!isAdminApproved || Boolean(paymentBlockedReason));
  const hasPreparedBankPaymentInfo = Boolean(
    registrationData?.va_numbers?.length > 0 ||
    registrationData?.permata_va_number ||
    (registrationData?.bill_key && registrationData?.biller_code)
  );


  if (loading) {
    return <SkeletonLoader />;
  }

  if (error || !event || !selectedCategory) {
    return (
      <section className="w-full min-h-screen py-20 text-center text-slate-900 flex-col justify-center relative bg-white">
        <motion.h2
          className="text-xl sm:text-2xl md:text-3xl font-black uppercase mb-4 text-slate-900"
          initial={{ opacity: 0, y: -15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          KEGIATAN
        </motion.h2>
        <motion.p
          className="text-4xl sm:text-5xl md:text-7xl font-black mx-auto mb-16 text-brand-red px-4"
          initial={{ opacity: 0, y: -15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          {error || "TIDAK DITEMUKAN"}
        </motion.p>
        <Link to="/kegiatan" className="text-brand-red hover:underline font-bold uppercase tracking-widest text-sm">
          Kembali ke Daftar Kegiatan
        </Link>
      </section>
    );
  }

  return (
    <section className="w-full min-h-screen text-slate-900 relative bg-white">
      {/* Hero */}
      <div
        className="relative w-full h-[35vh]"
        style={{
          backgroundImage: 'url("/bg/bg3.jpg")',
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundAttachment: "fixed",
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-red-900/90 via-red-900/70 to-red-900/45" />
      </div>

      {/* Overlap Card */}
      <div className="relative z-10 -mt-20">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="bg-white rounded-2xl border border-slate-200 p-4 md:p-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 items-center">
                <div className="flex items-start gap-4">
                  <img
                    src="/logo/logocolor.svg"
                    alt="Ultra24"
                    className="h-16 md:h-20 w-auto flex-shrink-0"
                    onError={(e) => { e.target.style.display = "none"; }}
                  />
                  <div className="min-w-0">
                    <h1 className="text-3xl md:text-4xl font-black text-slate-900 leading-tight">
                      {selectedCategory.categoryName}
                    </h1>
                    <p className="text-base md:text-lg text-slate-600 mt-1">{event.title || event.eventName}</p>
                    <p className="text-base text-slate-400">{formatDate(selectedCategory.date || event.eventDateTimestamp || (eventSlug === "ULTRA24" ? "15 – 16 Agustus 2026" : null))}</p>
                  </div>
                </div>
                <div className="flex justify-around md:justify-center gap-12 md:border-t-0 md:border-l border-slate-100 pt-6 md:pt-0 md:pl-12">
                  <div className="text-center">
                    <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-slate-400">Start</p>
                    <p className="text-base font-semibold text-slate-900 mt-1">{selectedCategory.time || event.time || event.startTime || "-"}</p>
                  </div>
                  <div>
                    <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-slate-400">COT</p>
                    <p className="text-base font-semibold text-slate-900 mt-1">
                      {selectedCategory.cot != null ? `${selectedCategory.cot} Jam` : "-"}
                    </p>
                  </div>
                </div>

                <div className="flex flex-col md:items-end gap-3">

                  <div className="md:text-right">
                    <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-slate-400">Reg Fee</p>
                    <p className="text-4xl font-black text-slate-900 leading-none mt-1">
                      {formatDomesticPrice(selectedCategory.price)}
                    </p>
                    <p className="text-sm text-slate-400 mt-1">
                      Foreigner: {formatForeignerPrice(selectedCategory.foreignerPrice, selectedCategory.price)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 pt-4 pb-12">
        <motion.div
          className="max-w-6xl mx-auto space-y-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: "easeInOut" }}
        >
          {/* Registration Section */}
          {registrationStatus === "registered" ? (
            currentEventIdForRules === "ULTRA24" && paymentDisplayStatus !== "LUNAS" ? (
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <p className="text-lg font-bold text-slate-800">Pendaftaran Diterima</p>
                  <p className="text-sm text-slate-600 mt-1">Selesaikan pembayaran Anda di halaman My Registration.</p>
                </div>
                <Button to="/MyRegistration" variant="primary">
                  Selesaikan Pembayaran
                </Button>
              </div>
            ) : (
            <>
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                {paymentDisplayStatus === "LUNAS" ? (
                  <div>
                    <p className="text-lg font-bold text-green-600">Pembayaran Berhasil</p>
                    <p className="text-sm text-slate-600 mt-1">Pesanan Anda sudah terkonfirmasi dan aktif.</p>
                  </div>
                ) : requiresAdminApproval && isAdminRejected ? (
                  <div>
                    <p className="text-lg font-bold text-red-600">Pendaftaran Ditolak</p>
                    <p className="text-sm text-slate-600 mt-1">Silakan hubungi admin untuk detail penolakan.</p>
                  </div>
                ) : requiresAdminApproval && !isAdminApproved ? (
                  <div>
                    <p className="text-lg font-bold text-slate-700">Menunggu Approval Admin</p>
                    <p className="text-sm text-slate-600 mt-1">Pembayaran akan aktif otomatis setelah admin menyetujui pendaftaran Anda.</p>
                  </div>
                ) : (
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                    <div className="max-w-2xl">
                      <p className="text-lg font-bold text-slate-700">
                        {isRegisteredInOtherCategory ? "Selesaikan Pendaftaran" : "Submission Diterima"}
                      </p>
                      <p className="text-sm text-slate-600 mt-1 leading-relaxed">
                        {isRegisteredInOtherCategory ? (
                          <>
                            Lengkapi pembayaran kategori <strong>{registrationData?.kategori}</strong> Anda di bawah ini. Jika ingin mengganti kategori, silakan cancel submission saat ini.
                          </>
                        ) : (
                           "Pendaftaran Anda sudah tersimpan. Selesaikan pembayaran di bawah ini untuk mengamankan slot."
                        )}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1">
                  <article className="bg-white rounded-xl border border-slate-200 p-5">
                    <h3 className="text-lg md:text-xl font-bold text-slate-800 mb-4">Detail Pesanan</h3>

                    <div className="grid grid-cols-2 md:grid-cols-12 gap-4 mb-4">
                      <div className="col-span-2 md:col-span-4">
                        <div className="text-xs text-slate-400 mb-1">Participant Name</div>
                        <div className="text-base font-semibold text-slate-800">
                          {registrationData?.namaLengkap || user?.displayName || "-"}
                        </div>
                      </div>
                      <div className="col-span-2 md:col-span-3">
                        <div className="text-xs text-slate-400 mb-1">Jersey Size</div>
                        <div className="text-base font-semibold text-slate-800 uppercase">
                          {registrationData?.sizeJersey || "-"}
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-12 gap-4 py-4 border-t border-slate-100">
                      <div className="col-span-2 md:col-span-4">
                        <div className="text-xs text-slate-400 mb-1">Order ID</div>
                        <div className="text-sm font-medium text-slate-700 md:whitespace-nowrap break-words">
                          {registrationData?.orderId || registrationData?.id || "-"}
                        </div>
                      </div>
                      <div className="col-span-2 md:col-span-3">
                        <div className="text-xs text-slate-400 mb-1">Category</div>
                        <div className="text-sm font-medium text-slate-700 break-words leading-snug">
                          {registrationData?.kategori || selectedCategory?.categoryName || "-"}
                        </div>
                      </div>
                      <div className="col-span-1 md:col-span-3">
                        <div className="text-xs text-slate-400 mb-1">Transaction Time</div>
                        <div className="text-sm font-medium text-slate-700 md:whitespace-nowrap">
                          {formatDateTime(registrationData?.transaction_time || registrationData?.createdAt)}
                        </div>
                      </div>
                      <div className="col-span-1 md:col-span-2">
                        <div className="text-xs text-slate-400 mb-1">Status</div>
                        <div className={`text-sm font-medium italic ${
                          paymentDisplayStatus === "LUNAS" ? "text-red-600" : "text-slate-500"
                        }`}>
                          {paymentDisplayStatus === "LUNAS" ? "Paid" : "Registered"}
                        </div>
                      </div>
                    </div>

                    {paymentDisplayStatus !== "LUNAS" && (
                      <div className="pt-3 pb-2 border-t border-slate-100">
                        <div className="mb-4">
                          <label className="text-slate-800 font-medium mb-2 block text-sm">Select Payment Method</label>
                          <div className="grid grid-cols-2 gap-3">
                            <button
                              type="button"
                              onClick={() => setPaymentMethod("midtrans")}
                              className={`flex flex-col items-center justify-center gap-1 p-4 rounded-xl border-2 transition-all duration-300 ${
                                paymentMethod === "midtrans"
                                  ? "border-red-600 bg-red-50 text-red-700 shadow-sm"
                                  : "border-slate-100 bg-slate-50 text-slate-400 hover:border-slate-200"
                              }`}
                            >
                              <span className="text-xs font-black uppercase tracking-widest">BANK</span>
                              <span className="text-[10px] opacity-70">Local Gateway</span>
                            </button>
                            <button
                              type="button"
                              onClick={() => setPaymentMethod("paypal")}
                              className={`flex flex-col items-center justify-center gap-1 p-4 rounded-xl border-2 transition-all duration-300 ${
                                paymentMethod === "paypal"
                                  ? "border-blue-500 bg-blue-50 text-blue-700 shadow-sm"
                                  : "border-slate-100 bg-slate-50 text-slate-400 hover:border-slate-200"
                              }`}
                            >
                              <span className="text-xs font-black uppercase tracking-widest">PAYPAL/CC</span>
                              <span className="text-[10px] opacity-70">Global (USD)</span>
                            </button>
                          </div>
                        </div>
                        <VoucherInput
                             eventId={event?.id || currentEventIdForRules}
                             kategori={registrationData?.kategori || selectedCategory?.categoryName}
                             disabled={paymentDisplayStatus === "LUNAS"}
                             onApplyVoucher={(code, discount, discountType) => {
                               setAppliedVoucher({ code, discount, discountType: discountType || "percentage" });
                             }}
                             onCodeChange={(val) => {
                               setCurrentVoucherInput(val);
                             }}
                        >
                            {paymentMethod === "midtrans" && (
                              <Button
                                onClick={handlePayNow}
                                variant="primary"
                                loading={isCreatingTransaction}
                                disabled={
                                  !isSnapReady || 
                                  isPaymentLockedByApproval || 
                                  (currentVoucherInput && currentVoucherInput.trim() !== "" && (!appliedVoucher || appliedVoucher.code !== currentVoucherInput))
                                }
                                className="px-6 md:min-w-[140px] uppercase"
                              >
                                {isCreatingTransaction ? "Menyiapkan..." : (getFinalPrice(Number(registrationData?.gross_amount || registrationData?.harga || selectedCategory?.price), appliedVoucher) === 0 ? "Complete Reg" : "Pay Now")}
                              </Button>
                            )}
                        </VoucherInput>
                      </div>
                    )}
                  </article>
                </div>

                <div className="w-full md:w-1/4 max-w-sm">
                  <aside className="bg-white p-4 rounded-xl border border-slate-200 flex flex-col gap-4 h-full">
                    <h3 className="text-lg md:text-xl font-bold text-slate-800 uppercase mb-4">EVENT DETAILS</h3>
                    <div className="text-xs text-slate-400 uppercase tracking-wider mb-2">CATEGORY</div>
                    <h4 className="text-xl font-black text-slate-800 uppercase">
                      {registrationData?.kategori || selectedCategory?.categoryName || "-"}
                    </h4>
                    <div className="mt-4 flex items-center gap-2 py-4 border-t border-b border-slate-100">
                      <div className="text-center flex-1">
                        <p className="text-[10px] text-slate-400 font-bold uppercase mb-1">CUT OFF</p>
                        <p className="text-sm font-bold text-slate-700 whitespace-nowrap">
                          {selectedCategory?.cot ? `${selectedCategory.cot} HOURS` : "- HOURS"}
                        </p>
                      </div>
                      <div className="h-8 w-px bg-slate-200"></div>
                      <div className="text-center flex-1">
                        <p className="text-[10px] text-slate-400 font-bold uppercase mb-1">PRICE</p>
                        <p className="text-sm font-bold text-slate-700 whitespace-nowrap">
                          {(() => {
                            const originalPrice = Number(registrationData?.gross_amount || registrationData?.harga || selectedCategory?.price);
                            const finalPrice = getFinalPrice(originalPrice, appliedVoucher);
                            if (appliedVoucher) {
                              return (
                                <div className="flex flex-col items-center">
                                  <span className="text-xs text-rose-500 line-through">
                                    Rp {originalPrice.toLocaleString("id-ID")}
                                  </span>
                                  <span className="text-sm text-red-600">
                                    Rp {finalPrice.toLocaleString("id-ID")}
                                  </span>
                                </div>
                              );
                            }
                            return formatDomesticPrice(originalPrice);
                          })()}
                        </p>
                      </div>
                    </div>

                    {paymentMethod === "paypal" && paymentDisplayStatus !== "LUNAS" && (
                      <div className="mt-4 pt-4 border-t border-slate-100">
                        <PayPalButton
                          orderId={registrationData?.orderId || registrationData?.id}
                          eventId={event?.id || "ULTRA24"}
                          voucherCode={appliedVoucher?.code || registrationData?.voucherCode}
                          onSuccess={async () => {
                            showNotification("success", "PayPal payment successful!");
                            await checkRegistration(collectionName, event.id);
                          }}
                          onError={(msg) => showNotification("error", msg || "PayPal error")}
                        />
                      </div>
                    )}
                  </aside>
                </div>
              </div>
            </>
            )
          ) : registrationStatus === null ? (
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-6 animate-pulse">
              <div className="h-5 bg-slate-200 rounded w-40 mb-3"></div>
              <div className="h-4 bg-slate-200 rounded w-64"></div>
            </div>
          ) : (
            <>
              {/* Banner Kualifikasi — tampil sebelum form registrasi */}
              <QualificationBanner categoryName={selectedCategory?.categoryName} />
              <InlineRegistrationForm
                event={event}
                selectedCategory={selectedCategory}
                collectionName={collectionName}
                isSnapReady={isSnapReady}
                onRegistrationComplete={handleRegistrationComplete}
              />
            </>
          )}
        </motion.div>
      </div>
    </section>
  );
});
